import { isEmpty } from 'lodash';
import { makeRetriableGetRequest } from '../common/api/apiActions';
import { RCT_ACCURACY_FETCH_RETRY, RCT_FLU_COMP_FETCH_RETRY } from '../common/api/apiRetryGetConstants';
import { apiCallFailed, noticeApiError } from '../common/api/apiCallActions';
import DNOServiceClient from '../common/clients/DNOServiceClient';
import {
    RCT, ACCURACY, FLUENCY, COMPREHENSION, FLUENCY_COMPREHENSION, FLUENCY_PASSAGE_QUESTION_SKIPPED,
} from '../constants/measure';
import selectors from '../rootSelectors';
import {
    saveMeasureResponse, createResult, discontinueMeasure, deleteResult,
} from '../common/api/dnoActions';
import { QUESTIONS, PASSAGE } from '../constants/assessmentItemType';
import * as screenActions from '../common/screen/screenActions';
import { ERROR_STATUS, LANTERN_ERROR_CODES } from '../constants/error';

const rctMeasure = RCT.toUpperCase();

export const SET_RCT_RESULT_ID = 'SET_RCT_RESULT_ID';
export const SET_RCT_FLU_COMP_CONTENT = 'SET_RCT_FLU_COMP_CONTENT';
export const SET_RCT_ACCURACY_CONTENT = 'SET_RCT_ACCURACY_CONTENT';
export const SHOW_QUESTION = 'SHOW_QUESTION';
export const HIDE_QUESTION = 'HIDE_QUESTION';
export const SHOW_SEGMENT = 'SHOW_SEGMENT';
export const HIDE_SEGMENT = 'HIDE_SEGMENT';
export const UPDATE_SELECTED_COMPREHENSION_RESPONSES = 'UPDATE_SELECTED_COMPREHENSION_RESPONSES';
export const RESET_SELECTED_COMPREHENSION_RESPONSES = 'RESET_SELECTED_COMPREHENSION_RESPONSES';
export const UPDATE_QUESTION_INDEX = 'UPDATE_QUESTION_INDEX';
export const RESET_QUESTION_INDEX = 'RESET_QUESTION_INDEX';
export const SET_IS_READY_SCRIPT_PLAYED = 'SET_IS_READY_SCRIPT_PLAYED';

const accuracyResponse = (formId, duration, responseId, itemId, itemIndex) => ({
    time_duration: duration,
    response_id: responseId,
    item_id: itemId,
    form_id: formId,
    item_idx: itemIndex,
});

const fluencyResponse = (formId, passageId, duration, segmentIdx,
    markPassageComplete = false, itemId, responseIndex) => {
    const response = {
        passage_idx: passageId,
        time_duration: duration,
        segment_idx: segmentIdx,
        form_id: formId,
        mark_passage_complete: markPassageComplete,
    };

    if (responseIndex === FLUENCY_PASSAGE_QUESTION_SKIPPED.index) {
        response.response_id = FLUENCY_PASSAGE_QUESTION_SKIPPED.responseId;
        response.outcome = FLUENCY_PASSAGE_QUESTION_SKIPPED.outcome;
    } else if (responseIndex !== undefined) {
        response.response_id = responseIndex;
    }

    if (itemId !== undefined) {
        response.item_id = itemId;
    }
    return response;
};

const comprehensionResponse = (formId, passageId, itemId, responseId, itemIdx,
    markPassageComplete) => ({
    item_idx: itemIdx,
    passage_idx: passageId,
    form_id: formId,
    response_id: responseId,
    item_id: itemId,
    mark_passage_complete: markPassageComplete,
});

export const saveComprehensionResponse = (passageId) => (dispatch, getStore) => {
    const state = getStore();
    const measureType = COMPREHENSION;
    const formId = selectors.getFluCompFormId(state);
    const questionIndex = selectors.getQuestionIndex(state);
    const totalQuestions = selectors.getComprehensionQuestionCount(state, passageId);
    const question = selectors.getRCTFluCompItems(state, COMPREHENSION, passageId)[questionIndex];
    const responseIndex = selectors.getSelectedComprehensionResponses(state)[questionIndex];
    const isPassageComplete = (questionIndex === (totalQuestions - 1));
    const isResultComplete = (passageId === (selectors.getPassagesCount(state) - 1))
            && isPassageComplete;

    const resultResponse = comprehensionResponse(
        formId,
        passageId,
        question.id,
        question.possibleResponses[responseIndex].responseId,
        questionIndex,
        isPassageComplete,
    );

    return dispatch(
        saveMeasureResponse(rctMeasure, measureType, formId, resultResponse, isResultComplete),
    );
};

export const saveFluencyResponse = (passageId) => (dispatch, getStore) => {
    const state = getStore();
    const measureType = FLUENCY;
    const formId = selectors.getFluCompFormId(state);
    const selectedResponseIndex = selectors.getSelectedIndex(state);
    const question = selectors.getCurrentQuestion(state, PASSAGE, passageId);
    const responseId = selectedResponseIndex === FLUENCY_PASSAGE_QUESTION_SKIPPED.index
        ? selectedResponseIndex
        : question.possibleResponses[selectedResponseIndex].id;
    const segmentIndex = selectors.getActiveSegmentIndex(state);
    const duration = selectors.getQuestionTimeTrackingDuration(state);
    const isPassageComplete = !selectors.hasNextSegment(state, PASSAGE, passageId);

    const resultResponse = fluencyResponse(
        formId,
        passageId,
        duration,
        segmentIndex,
        isPassageComplete,
        question.stimulus.id,
        responseId,
    );

    return dispatch(saveMeasureResponse(
        rctMeasure, measureType, formId, resultResponse, isPassageComplete,
    ));
};

export const saveFluencySegmentReadingTime = (passageId) => (dispatch, getStore) => {
    const state = getStore();
    const passageHasQuestion = selectors.hasQuestion(state, PASSAGE, passageId);

    if (passageHasQuestion) {
        return Promise.resolve(passageHasQuestion);
    }

    const measureType = FLUENCY;
    const formId = selectors.getFluCompFormId(state);
    const segmentIndex = selectors.getActiveSegmentIndex(state);
    const duration = selectors.getQuestionTimeTrackingDuration(state);
    const isPassageComplete = !selectors.hasNextSegment(state, PASSAGE, passageId);
    const isResultComplete = (passageId === (selectors.getPassagesCount(state) - 1))
            && isPassageComplete;
    const resultResponse = fluencyResponse(
        formId,
        passageId,
        duration,
        segmentIndex,
        isPassageComplete,
    );

    return dispatch(
        saveMeasureResponse(rctMeasure, measureType, formId, resultResponse, isResultComplete),
    )
        .then(() => passageHasQuestion);
};

export const createFluencyComprehensionResults = () => (dispatch, getStore) => {
    const state = getStore();
    const formId = selectors.getFluCompFormId(state);
    const fluencyResult = selectors.findResultFor(state,
        { measure: rctMeasure, measureType: FLUENCY, formId });
    const comprehensionResult = selectors.findResultFor(state,
        { measure: rctMeasure, measureType: COMPREHENSION, formId });
    const fluencyPromise = fluencyResult
        ? Promise.resolve() : dispatch(createResult(rctMeasure, FLUENCY, formId));
    const comprehensionPromise = comprehensionResult
        ? Promise.resolve() : dispatch(createResult(rctMeasure, COMPREHENSION, formId));
    return Promise.all([fluencyPromise, comprehensionPromise]);
};

export const discontinueFluencyComprehension = () => (dispatch, getStore) => {
    const state = getStore();
    const formId = selectors.getFluCompFormId(state);
    return Promise.all([
        dispatch(discontinueMeasure(rctMeasure, FLUENCY, formId)),
        dispatch(discontinueMeasure(rctMeasure, COMPREHENSION, formId)),
    ]);
};

export const saveAccuracyResponse = (questionIndex) => (dispatch, getStore) => {
    const state = getStore();
    const formId = selectors.getAccuracyFormId(state);
    const measureType = ACCURACY;
    const totalQuestions = selectors.getRCTAccuracyTotalQuestions(state);
    const isComplete = questionIndex === totalQuestions;
    const question = selectors.getRCTAccuracyContent(state, QUESTIONS, questionIndex - 1);
    const selectedResponseIndex = selectors.getSelectedIndex(state);
    // NOTE: limit time to be max as 20s
    // this is done as backend does not allow time duration > 20s
    // front end has no auto-advance feature implemented yet
    // done for user protection from a crash
    const duration = Math.min(selectors.getQuestionTimeTrackingDuration(state), 20);
    const resultResponse = accuracyResponse(
        formId,
        duration,
        question.possibleResponses[selectedResponseIndex].id,
        question.itemId,
        questionIndex - 1,
    );
    return dispatch(
        saveMeasureResponse(rctMeasure, measureType, formId, resultResponse, isComplete),
    );
};

export const setRCTAccuracyContent = (content) => ({
    type: SET_RCT_ACCURACY_CONTENT,
    content,
});

export const setFluencyComprehensionContent = (content) => ({
    type: SET_RCT_FLU_COMP_CONTENT,
    content,
});

const fetchContent = (measure, contentMeasureType, resultMeasureType, setContentAction,
    retryGetRequestActionName) => (dispatch, getStore) => {
    const state = getStore();

    const resumeResultFormId = selectors.getResumeResultFormId(state,
        {
            measure: DNOServiceClient.CLIENT_TO_SERVER_MEASURE_NAME[measure],
            measureType: resultMeasureType,
            isComplete: false,
        });

    let endpoint;
    if (resumeResultFormId) {
        // We already have a form id from result submitted in previous assessment session,
        // use that formId to get the content
        const config = {
            measure,
            measureType: contentMeasureType,
            formId: resumeResultFormId,
        };
        endpoint = DNOServiceClient.constructFormEndpoint(config);
    } else {
        const config = {
            measure,
            measureType: contentMeasureType,
            period: selectors.getToy(state),
            grade: selectors.getGrade(state),
            formVersion: selectors.getRctFormVersion(state),
        };
        endpoint = DNOServiceClient.constructFormEndpoint(config);
    }

    return dispatch(makeRetriableGetRequest(endpoint, retryGetRequestActionName))
        .then((contentJson) => {
            if (isEmpty(contentJson)) {
                dispatch(
                    noticeApiError(
                        ERROR_STATUS.NO_FORM_CONTENT,
                        LANTERN_ERROR_CODES.NO_FORM_CONTENT,
                        `${measure} ${contentMeasureType}`,
                    ),
                );
                dispatch(apiCallFailed(
                    undefined,
                    undefined,
                    ERROR_STATUS.NO_FORM_CONTENT,
                    LANTERN_ERROR_CODES.NO_FORM_CONTENT,
                ));
            } else {
                const content = contentJson instanceof Array ? contentJson[0] : contentJson;
                dispatch(setContentAction(content));
            }
        });
};

export const fetchRctAccuracyContent = () => fetchContent(RCT, ACCURACY, ACCURACY, setRCTAccuracyContent,
    RCT_ACCURACY_FETCH_RETRY);

export const fetchRctFluCompContent = () => fetchContent(RCT, FLUENCY_COMPREHENSION, COMPREHENSION, setFluencyComprehensionContent,
    RCT_FLU_COMP_FETCH_RETRY);

export const showQuestion = () => ({
    type: SHOW_QUESTION,
});

export const hideQuestion = () => ({
    type: HIDE_QUESTION,
});

export const showSegment = (index) => ({
    type: SHOW_SEGMENT,
    index,
});

export const hideSegment = () => ({
    type: HIDE_SEGMENT,
});

export const updateSelectedComprehensionResponses = (questionIndex, responseIndex) => ({
    type: UPDATE_SELECTED_COMPREHENSION_RESPONSES,
    questionIndex,
    responseIndex,
});

export const resetSelectedComprehensionResponses = () => ({
    type: RESET_SELECTED_COMPREHENSION_RESPONSES,
});

export const updateQuestionIndex = (index) => ({
    type: UPDATE_QUESTION_INDEX,
    index,
});

export const resetQuestionIndex = () => ({
    type: RESET_QUESTION_INDEX,
});

export const setIsReadyScriptPlayed = (isPlayed) => ({
    type: SET_IS_READY_SCRIPT_PLAYED,
    isPlayed,
});

export const recreateFluCompResults = () => (dispatch, getStore) => {
    const formId = selectors.getFluCompFormId(getStore());
    dispatch(screenActions.blockScreen());
    const deleteResultPromise = Promise.all([
        dispatch(deleteResult(rctMeasure, FLUENCY, formId)),
        dispatch(deleteResult(rctMeasure, COMPREHENSION, formId)),
    ]);

    return deleteResultPromise.then(() => dispatch(createFluencyComprehensionResults())
        .then(() => {
            dispatch(screenActions.unblockScreen());
        }));
};
